import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  normaliseChinaPaymentDetail,
  normaliseChinaPaymentRecords,
  type ChinaPaymentRecordPage,
  type ChinaPaymentRequestDetail,
} from '@mypoz/shared';
import { useLocale } from '@mypoz/i18n';

import {
  fetchChinaPaymentDetail,
  fetchChinaPaymentRecordsPage,
} from '@/features/china-payment/api.js';

export interface ChinaPaymentRecordsState {
  readonly status: 'loading' | 'ready' | 'error';
  /** The last page that arrived; kept while the next one loads, so the list does not blink. */
  readonly page: ChinaPaymentRecordPage | undefined;
  readonly retry: () => void;
}

/**
 * One page of the China Payment request history (MYP-63), asked of the vendor page by page
 * (`getalipaylist` takes `page` and `limit`). The raw answer is kept, so a language switch
 * relabels the rows without asking again.
 */
export function useChinaPaymentRecords(page: number, size: number): ChinaPaymentRecordsState {
  const { locale } = useLocale();
  const language = locale === 'zh' ? 'zh' : 'en';
  const [body, setBody] = useState<unknown>(undefined);
  const [status, setStatus] = useState<ChinaPaymentRecordsState['status']>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Only the newest request's answer lands (a quick page change, StrictMode, or a retry).
    let current = true;
    setStatus('loading');
    fetchChinaPaymentRecordsPage(page, size).then(
      (next) => {
        if (!current) return;
        setBody(next);
        setStatus('ready');
      },
      (cause: unknown) => {
        if (!current) return;
        console.error('[china-payment] could not load the request history', cause);
        setStatus('error');
      },
    );
    return () => {
      current = false;
    };
  }, [page, size, attempt]);

  const normalised = useMemo(
    () => (body === undefined ? undefined : normaliseChinaPaymentRecords(body, language)),
    [body, language],
  );
  const retry = useCallback(() => setAttempt((previous) => previous + 1), []);

  return { status, page: normalised, retry };
}

export interface ChinaPaymentDetailState {
  /** `missing`: the vendor has no such request for this customer. */
  readonly status: 'loading' | 'ready' | 'missing' | 'error';
  readonly detail: ChinaPaymentRequestDetail | undefined;
  readonly retry: () => void;
}

/** One request with the details the customer filled in (`alipaydetail`), when its sheet opens. */
export function useChinaPaymentDetail(id: string): ChinaPaymentDetailState {
  const { locale } = useLocale();
  const language = locale === 'zh' ? 'zh' : 'en';
  const [body, setBody] = useState<unknown>(undefined);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let current = true;
    setBody(undefined);
    setFailed(false);
    fetchChinaPaymentDetail(id).then(
      (next) => {
        if (current) setBody(next);
      },
      (cause: unknown) => {
        if (!current) return;
        console.error('[china-payment] could not load a request', cause);
        setFailed(true);
      },
    );
    return () => {
      current = false;
    };
  }, [id, attempt]);

  const detail = useMemo(
    () => (body === undefined ? undefined : normaliseChinaPaymentDetail(body, language)),
    [body, language],
  );
  const retry = useCallback(() => setAttempt((previous) => previous + 1), []);
  const status = failed ? 'error' : body === undefined ? 'loading' : detail ? 'ready' : 'missing';

  return { status, detail, retry };
}
